import React from 'react';
import { CheckboxInput } from '@instinet-analytics/semantic-ui-components';
import { Dropdown } from '@instinet-analytics/semantic-ui-components';

import './AppSettingsModal.scss';
import {
    AppSettingsState,
    currencyOptions,
    symbologyOptions,
    SymbologyValues,
    mapUserOptionsToDropdown,
} from 'app/store/appSettings/AppSettingsState';
import {
    useFetchIndexMappingsUserPreference,
    useSelectIndexMappingsUserPreferenceWithoutDefaulting,
} from 'symbols/indexMappings/hooks';
import { useIndexMappingsDisplayText } from 'symbols/hooks/useIndexMappingsDisplayText';
import { IndexMappingsEditorModal } from 'symbols/indexMappings/IndexMappingsEditorModal/IndexMappingsEditorModal';
import { HelpPortal } from 'utils/components/helpPortal/HelpPortal';

export interface AppSettingsSpecifiedControls {
    combineSellsAndShorts?: boolean;
    currency?: boolean;
    symbology?: boolean;
    alwaysShowMenuBar?: boolean;
    showLayoutTitle?: boolean;
    showsBuysAsPositive?: boolean;
    showTooltips?: boolean;
    showChartHelp?: boolean;
    showIndexMappings?: boolean;
    showCategoryTitles?: boolean;
}

const DefaultAppSettingSpecifiedControls: AppSettingsSpecifiedControls = {
    alwaysShowMenuBar: true,
    combineSellsAndShorts: true,
    currency: true,
    symbology: true,
    showLayoutTitle: true,
    showsBuysAsPositive: true,
    showTooltips: true,
    showChartHelp: true,
    showIndexMappings: true,
    showCategoryTitles: true,
};

export interface AppSettingsEditorProps {
    appSettings: AppSettingsState;
    /*
     * The specified controls to show. If not provided, the settings appropriate for the support modal in standalone mode are used
     */
    specifyVisibleControls?: AppSettingsSpecifiedControls;
    onAppSettingsChanged: (delta: Partial<AppSettingsState>) => void;
}

export default function AppSettingsEditor(props: AppSettingsEditorProps) {
    const { onAppSettingsChanged, appSettings } = props;

    const specifiedControls =
        props.specifyVisibleControls || DefaultAppSettingSpecifiedControls;

    const {
        alwaysShowMenuBar,
        currencyCode,
        symbology,
        combineSellsAndShorts,
        showBuysAsPositive,
        hideChartHelp,
        hideTooltips,
    } = appSettings;

    const userPrefMappings =
        useSelectIndexMappingsUserPreferenceWithoutDefaulting();
    useFetchIndexMappingsUserPreference();
    const userPrefsDisplay = useIndexMappingsDisplayText();

    const toggleSetting = (key: keyof AppSettingsState) => () => {
        onAppSettingsChanged({
            [key]: !appSettings[key],
        });
    };

    const currencyDropdownValues = mapUserOptionsToDropdown(currencyOptions);

    const onCurrencyCodeChanged = (currencyCode: string) =>
        onAppSettingsChanged({ currencyCode });

    const symbologyDropdownValues = mapUserOptionsToDropdown(symbologyOptions);

    const onSymbologyChanged = (symbology: SymbologyValues) =>
        onAppSettingsChanged({ symbology });

    return (
        <>
            {specifiedControls.showIndexMappings && (
                <div className="group">
                    {specifiedControls.showCategoryTitles && (
                        <div className="group-header">
                            <div className="category-title">
                                Indigo Preferences{' '}
                                <HelpPortal height={60} width={310} left={-140}>
                                    These settings will be saved in your Indigo
                                    user preferences, and will apply across all
                                    of your layouts.
                                </HelpPortal>
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="title">Symbol to Index Mappings</div>
                        <div className="symbol-index-mappings">
                            <div className="value user-prefs">
                                {userPrefsDisplay}
                            </div>
                            {userPrefMappings && <IndexMappingsEditorModal />}
                        </div>
                    </div>
                </div>
            )}
            <div className="group">
                {specifiedControls.showCategoryTitles && (
                    <>
                        <div className="group-header">
                            <div className="category-title">
                                Layout Preferences{' '}
                                <HelpPortal height={45} width={180} left={-140}>
                                    These settings will apply for your current
                                    layout only.
                                </HelpPortal>
                            </div>
                        </div>
                        <div className="group-header">
                            <div className="title">General</div>
                        </div>
                    </>
                )}
                {specifiedControls.alwaysShowMenuBar && (
                    <div>
                        <CheckboxInput
                            label="Always show menu bar"
                            checked={alwaysShowMenuBar}
                            onToggle={toggleSetting('alwaysShowMenuBar')}
                        />
                    </div>
                )}

                {specifiedControls.combineSellsAndShorts && (
                    <div>
                        <CheckboxInput
                            className="combine-sells-and-shorts"
                            label="Combine sells and shorts"
                            checked={combineSellsAndShorts}
                            onToggle={toggleSetting('combineSellsAndShorts')}
                        />
                    </div>
                )}

                {specifiedControls.showsBuysAsPositive && (
                    <div>
                        <CheckboxInput
                            className="show-buys-as-positive"
                            label="Show buys as positive"
                            checked={showBuysAsPositive}
                            onToggle={toggleSetting('showBuysAsPositive')}
                        />
                    </div>
                )}

                {specifiedControls.currency && (
                    <div>
                        <div>Currency</div>
                        <div>
                            <Dropdown
                                className="currency-dropdown"
                                options={currencyDropdownValues}
                                value={currencyCode}
                                onChange={onCurrencyCodeChanged}
                            />
                        </div>
                    </div>
                )}

                {specifiedControls.symbology && (
                    <div>
                        <div>Symbology</div>
                        <div>
                            <Dropdown
                                className="symbology-dropdown"
                                options={symbologyDropdownValues}
                                value={symbology}
                                onChange={onSymbologyChanged}
                            />
                        </div>
                    </div>
                )}

                {(specifiedControls.showTooltips ||
                    specifiedControls.showChartHelp) && (
                    <div className="group-header">Help</div>
                )}

                {specifiedControls.showTooltips && (
                    <div>
                        <div>
                            <CheckboxInput
                                className="show-tooltips"
                                label="Legend Descriptions (tooltips)"
                                checked={!hideTooltips}
                                onToggle={toggleSetting('hideTooltips')}
                            />
                        </div>
                    </div>
                )}

                {specifiedControls.showChartHelp && (
                    <div>
                        <div>
                            <CheckboxInput
                                className="show-chart-help"
                                label="Chart Descriptions"
                                checked={!hideChartHelp}
                                onToggle={toggleSetting('hideChartHelp')}
                            />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
